import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { logout } from '../../api/endpoints/auth.ts';
import { useAuthStore } from '../../stores/auth-store.ts';
import { useUIStore } from '../../stores/ui-store.ts';
import { Sun, Moon, Monitor, LogOut, MoreHorizontal } from 'lucide-react';
import { NotificationBell } from '../notifications/NotificationBell.tsx';
import { NoPanelLogo } from '@/components/brand/NoPanelLogo';
import { SpatialCommandSlot } from '@/components/spatial/spatial-command-slot.tsx';
import { frameCopy } from '@/components/layout/frame-copy';
import { useSignoff } from '@/components/layout/signoff';
import {
  isSpatialDockOverflowPath,
  spatialDockOverflow,
  spatialDockPrimary,
} from '@/lib/spatial-nav.ts';
import type { Theme } from '../../types/app.ts';

const NAV_SUB: Record<string, string> = {
  '/': 'npc',
  '/servers': 'server',
  '/servers/runtime': 'env',
  '/projects/runs': 'deploy',
};

const themeIcons: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const themeOrder: Theme[] = ['light', 'dark', 'system'];

export function Header({ onPalette }: { onPalette: () => void }) {
  const { t, i18n } = useTranslation();
  const copy = frameCopy(i18n.language);
  const location = useLocation();
  const { username, avatarUrl, clearAuth } = useAuthStore();
  const { locale, setLocale, theme, setTheme } = useUIStore();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [navMoreOpen, setNavMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const navMoreRef = useRef<HTMLDivElement>(null);
  const overflow = spatialDockOverflow();
  const overflowActive = isSpatialDockOverflowPath(location.pathname);
  const { waiting, openSheet } = useSignoff();

  const handleLogout = async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (refreshToken) {
      try { await logout(refreshToken); } catch { /* ignore */ }
    }
    clearAuth();
    navigate('/login', { replace: true });
  };

  const cycleTheme = () => {
    const idx = themeOrder.indexOf(theme);
    setTheme(themeOrder[(idx + 1) % themeOrder.length]);
  };

  useEffect(() => {
    setNavMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen && !navMoreOpen) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (moreOpen && !moreRef.current?.contains(target)) setMoreOpen(false);
      if (navMoreOpen && !navMoreRef.current?.contains(target)) setNavMoreOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMoreOpen(false);
        setNavMoreOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen, navMoreOpen]);

  const ThemeIcon = themeIcons[theme];

  return (
    <header className="spatial-top-rail">
      <NoPanelLogo showWordmark markClassName="h-[22px] w-[22px]" />
      <nav className="np-nav" aria-label={t('sidebar.desktopIcons')}>
        {spatialDockPrimary.map((item) => {
          const active = item.isActive ? item.isActive(location.pathname) : location.pathname === item.to;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? item.to === '/'}
              className={() => `np-nav-link${active ? ' is-active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              {t(item.labelKey)}
              <small>{NAV_SUB[item.to] ?? ''}</small>
            </NavLink>
          );
        })}
        <span className="np-nav-sep" aria-hidden="true" />
        <div className="np-more" ref={navMoreRef}>
          <button
            type="button"
            data-active={overflowActive || navMoreOpen ? '' : undefined}
            aria-expanded={navMoreOpen}
            aria-haspopup="menu"
            onClick={() => setNavMoreOpen((open) => !open)}
          >
            {t('sidebar.more')}
            <small>…</small>
          </button>
          {navMoreOpen ? (
            <div className="np-menu" role="menu">
              {overflow.map((section) => (
                <div key={section.labelKey}>
                  <h3>{t(section.labelKey)}</h3>
                  {section.items.map((item) => {
                    const active = item.isActive ? item.isActive(location.pathname) : location.pathname === item.to;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        role="menuitem"
                        className={active ? 'is-active' : undefined}
                        onClick={() => setNavMoreOpen(false)}
                      >
                        {t(item.labelKey)}
                      </NavLink>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </nav>
      <button type="button" className="kbar" onClick={onPalette} aria-label={copy.palette}>
        <span>$</span>
        <em>{copy.palette}</em>
        <kbd>⌘K</kbd>
      </button>
      <SpatialCommandSlot />
      {waiting.length > 0 ? (
        <button type="button" className="pending-pill" onClick={() => openSheet()}>
          <b>{waiting.length}</b>
          <span>{copy.signoff}</span>
        </button>
      ) : null}
      <div className="spatial-top-rail-account spatial-top-rail-account-wide">
        <NotificationBell />
        <button
          onClick={cycleTheme}
          className="inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-[var(--hover)] hover:text-foreground"
          aria-label={`Theme: ${theme}`}
        >
          <ThemeIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => setLocale(locale === 'zh' ? 'en' : 'zh')}
          className="inline-flex h-11 min-w-11 items-center justify-center rounded-lg px-3 text-body font-medium text-muted-foreground hover:bg-[var(--hover)] hover:text-foreground"
          aria-label="Switch language"
        >
          {locale === 'zh' ? 'EN' : '中'}
        </button>
        {username && (
          <div className="flex items-center gap-1">
            {avatarUrl ? (
              <img src={avatarUrl} alt={username} className="h-7 w-7 rounded-full" />
            ) : (
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-[11px] font-medium text-background">
                {username[0]?.toUpperCase()}
              </div>
            )}
            <span className="spatial-top-rail-username text-ui text-foreground">{username}</span>
            <button
              onClick={handleLogout}
              className="inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-[var(--hover)] hover:text-foreground"
              aria-label={t('header.signOut')}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      <div className="spatial-top-rail-more" ref={moreRef}>
        <button
          type="button"
          className="inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-[var(--hover)] hover:text-foreground"
          aria-label={t('header.more')}
          aria-expanded={moreOpen}
          aria-haspopup="menu"
          onClick={() => setMoreOpen((open) => !open)}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
        {moreOpen ? (
          <div className="spatial-top-rail-menu" role="menu">
            <div className="flex items-center gap-1 px-1">
              <NotificationBell />
              <button
                onClick={cycleTheme}
                className="inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-[var(--hover)] hover:text-foreground"
                aria-label={`Theme: ${theme}`}
              >
                <ThemeIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setLocale(locale === 'zh' ? 'en' : 'zh')}
                className="inline-flex h-11 min-w-11 items-center justify-center rounded-lg px-3 text-body font-medium text-muted-foreground hover:bg-[var(--hover)] hover:text-foreground"
                aria-label="Switch language"
              >
                {locale === 'zh' ? 'EN' : '中'}
              </button>
              {username ? (
                <button
                  onClick={() => {
                    setMoreOpen(false);
                    void handleLogout();
                  }}
                  className="inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-[var(--hover)] hover:text-foreground"
                  aria-label={t('header.signOut')}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
}
